import React from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from '@agnes/ui';
import { usePermissionsStore } from '@/stores/usePermissionsStore';
import { useAuthStore } from '@/stores/useAuthStore';
import ServiceAuthSSO from '@/services/ServiceAuthSSO';
import { authStorage } from '@/lib/authStorage';

type Status = 'checking' | 'allowed' | 'no-auth' | 'no-perm';

export function RequirePermission({
  code,
  children,
}: Readonly<{ code: string | string[]; children: React.ReactNode }>) {
  const [status, setStatus] = React.useState<Status>('checking');

  React.useEffect(() => {
    let active = true;
    (async () => {
      if (!authStorage.getItem('access_Token')) {
        if (active) setStatus('no-auth');
        return;
      }
      await useAuthStore.getState().getUser();
      let loaded = await usePermissionsStore.getState().fetchPermissions();
      if (!active) return;

      if (!loaded && authStorage.getItem('access_Token')) {
        loaded = await usePermissionsStore.getState().fetchPermissions();
        if (!active) return;
      }
      if (!authStorage.getItem('access_Token')) {
        setStatus('no-auth');
        return;
      }
      if (!loaded) {
        setStatus('no-perm');
        return;
      }

      const ok = usePermissionsStore.getState().hasAnyPermission(code);
      if (!ok) {
        toast.error(
          'Esta funcionalidade não está disponível no seu plano de serviço. Solicite este módulo para seu consultor da AGNES.',
        );
        setStatus('no-perm');
      } else {
        setStatus('allowed');
      }
    })();
    return () => {
      active = false;
    };
  }, [code]);

  if (status === 'checking') return null;
  if (status === 'no-auth')
    return <Navigate to={ServiceAuthSSO.isSSO() ? '/login/sso' : '/login'} replace />;
  if (status === 'no-perm') {
    // Nunca redireciona pra uma rota própria — evitaria loop (não há "Home" nesta
    // app React; o toast já informa o motivo, aqui só bloqueia o conteúdo).
    return (
      <div className="flex h-full items-center justify-center p-8 text-agnes-text-muted">
        Você não tem acesso a esta funcionalidade.
      </div>
    );
  }
  return <>{children}</>;
}
